/**
 * PredictedDocsBlock — Astor predicted-docs killer feature
 *
 * Mint-bordered panel that surfaces documents Astor predicts the borrower
 * will need next, with confidence percentages and per-row "Pre-stage" CTAs.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Astor surface 3 — KILLER FEATURE)
 *
 * Data: the parent (`DocumentsPage`) fetches predictions via
 * `useAstorPredictedDocs(caseId)` and passes them in. The backend lives in
 * `8-agent-service/src/astor-predicted-docs/AstorPredictedDocsService` —
 * Wave-2 deterministic projection (per-loan-flavor lookup table → drop
 * already-uploaded → sort desc by confidence, cap 6). Future LLM swap is
 * compatible with the same `predictions` prop shape.
 *
 * Rules:
 * - Glass components only (rule 11b)
 * - sx + theme tokens, alpha() for tints, baseColors for mint/amber (rule 11c)
 * - All copy via t() (rule 13)
 * - 44px touch target on every CTA (portals.md)
 *
 * TODO(#design-system-pipeline): replace inline rows with `<AstorPredictionCard>`
 *   once that lands in @ops-matrix/design-system-ui.
 */

import {
  alpha,
  Box,
  baseColors,
  GlassButton,
  GlassChip,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { AstorAvatar } from '../loans/AstorAvatar';

export interface PredictedDoc {
  /** Stable id from the predictions backend. */
  id: string;
  /** Human-readable document name (e.g. "2025 Schedule E"). */
  name: string;
  /** 0–100 confidence percentage. */
  confidence: number;
  /** Plain-English reason ("Based on your last filing cadence"). */
  reason: string;
}

export interface PredictedDocsBlockProps {
  predictions: PredictedDoc[];
  onPreStage: (prediction: PredictedDoc) => void;
}

export function PredictedDocsBlock({ predictions, onPreStage }: PredictedDocsBlockProps) {
  const { t } = useI18n();
  if (predictions.length === 0) return null;

  return (
    <Box
      role="region"
      aria-label={t('consumer.documents.predictedDocsTitle')}
      sx={{
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: alpha(baseColors.teal[400], 0.4),
        bgcolor: alpha(baseColors.teal[400], 0.04),
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
        <AstorAvatar size={24} />
        <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
          {t('consumer.documents.predictedDocsTitle')}
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        {predictions.map((prediction) => (
          <Box
            key={prediction.id}
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { md: 'center' },
              gap: 1.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {prediction.name}
                </Typography>
                <GlassChip
                  label={t('consumer.documents.predictedConfidence', { percent: prediction.confidence })}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Stack>
              <Typography variant="body2" color="textSecondary">
                {prediction.reason}
              </Typography>
            </Box>
            <GlassButton
              size="small"
              variant="outlined"
              onClick={() => onPreStage(prediction)}
              sx={{ minHeight: 44, flexShrink: 0 }}
            >
              {t('consumer.documents.predictedPreStage')}
            </GlassButton>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default PredictedDocsBlock;
